import {ReactElement, useEffect} from "react";
import {Controller} from "react-hook-form";
import {styled} from "styled-components";

import {HookFormContext, HookFormContextType} from "@/context/HookFormContext";
import {getValeurBonusUnite} from "@/fonction/getValeurBonusUnite";
import {quicksandSemiBold} from "@/GlobalStyle";
import {useGetContext} from "@/hooks/useGetContext/useGetContext";
import {FournisseurBonus} from "@/ts/kpi";

import SelectCustom from "../../Formulaire/SelectCustom/SelectCustom";

const Tableau = styled.div`
  background-color: ${({theme}) => theme.colors.darkPrimary};
  min-width: 210px;
  max-width: 380px;
  padding: 25px;
  height: fit-content;
  width: calc(100% - 50px);
  margin: 0 auto;
  & h3 {
    margin: 0 0 10px 0;
    color: white;
    text-align: center;
    font-size: 1.125rem;
    ${quicksandSemiBold}
  }
`;

const HeadTableau = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 15px;
  width: 100%;
  flex-wrap: wrap;
  & > div {
    min-width: 150px;
  }
`;
const Arrow = styled.div`
  position: relative;
  height: 24px;
  width: 50px;
  max-width: 50px;
  flex: none;
`;
const PointArrow = styled.div`
  width: 10px;
  height: 10px;
  border: solid ${({theme}) => theme.colors.darkPrimary};
  border-width: 0 2px 2px 0;
  display: inline-block;
  transform: rotate(-45deg);
  padding: 3px;
  flex: none;
  position: absolute;
  right: 1px;
  top: 3px;
`;

const TigeArrow = styled.div`
  width: 50px;
  height: 2px;
  background-color: ${({theme}) => theme.colors.darkPrimary};
  display: inline-block;
  position: absolute;
  top: 11px;
`;

const LigneBonus = styled.div<{$isLast: boolean}>`
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  max-width: 300px;
  margin: ${({$isLast}) => ($isLast ? "15px auto 0" : "15px auto")};
  padding: 10px 15px;
  justify-content: space-between;
  background-color: white;
  width: calc(100% - 20px);
`;
const TextBonus = styled.p`
  ${quicksandSemiBold}
  font-size: 1rem;
  flex: 1;
  padding: 0 10px;
  &:last-child {
    text-align: right;
  }
`;

const TexteInfo = styled.p`
  font-size: 0.75rem;
  color: ${({theme}) => theme.colors.white};
  text-align: left;
  margin-top: 10px;
  padding: 0 25px 0 25px;
`;

export function TableauBonus({
  bonus,
}: {
  bonus: FournisseurBonus[];
}): ReactElement {
  const {watch, control, setValue} = useGetContext<HookFormContextType>(
    HookFormContext,
    "HookFormContext"
  );
  const watchBonus = watch("selected_bonus");
  // Recupère les bonus et les affiche dans le tableau.
  const getBonusValue = (index: number): ReactElement[] => {
    const unite = getValeurBonusUnite(
      bonus?.[index]?.type as "pourcentage-ca" | "fixe"
    );

    return bonus
      ?.sort((a, b) => a.seuil_ca - b.seuil_ca)
      [index]?.paliers?.sort(
        (a, b) =>
          parseFloat(a.seuil.toString()) - parseFloat(b.seuil.toString())
      )
      .map((bonusValue, index) => {
        return (
          <LigneBonus $isLast={bonus.length >= index + 1} key={`${index}`}>
            <TextBonus>{bonusValue.seuil} KPI</TextBonus>
            <Arrow>
              <TigeArrow /> <PointArrow></PointArrow>
            </Arrow>
            <TextBonus>
              {bonusValue.valeur} {unite}
            </TextBonus>
          </LigneBonus>
        );
      });
  };
  const AllBonus = getBonusValue(watchBonus?.value);

  const options = bonus
    .map((bonusValue, index) => {
      return {
        libelle: `${bonusValue?.seuil_ca?.toString()} €`,
        value: index,
        seuil_ca: bonusValue?.seuil_ca,
      };
    })
    ?.sort((a, b) => a.seuil_ca - b.seuil_ca)
    .map(({libelle, value}) => ({libelle, value}));
  // affiche le bonus par default. Prend celui au seuil du cache. Si pas de cache ou pas de correspondance, prend le premier.
  const getDefaultValueBonus = (): {libelle: string; value: number} => {
    const cacheBonus = localStorage.getItem("selected_bonus");
    if (cacheBonus) {
      const cacheBonusObject = JSON.parse(cacheBonus);
      const finedIndexBonus = bonus?.findIndex(bonusValue => {
        return (
          `${bonusValue.seuil_ca.toString()} €` == cacheBonusObject.libelle
        );
      });
      if (finedIndexBonus < 0) {
        return {
          libelle: options?.[0]?.libelle.toString(),
          value: 0,
        };
      }
      return cacheBonusObject;
    }
    return {
      libelle: options?.[0]?.libelle.toString(),
      value: 0,
    };
  };

  useEffect(() => {
    setValue("selected_bonus", getDefaultValueBonus());
  }, []);
  return (
    <Tableau>
      <HeadTableau>
        <h3>Bonus - CA minimum :</h3>
        <Controller
          name="selected_bonus"
          control={control}
          render={({field}) => (
            <SelectCustom
              handleSelect={(value, libelle) => {
                const newBonus = {
                  libelle: libelle,
                  value,
                };

                // Déclenche le changement du champ via field.onChange
                field.onChange(newBonus);
                localStorage.setItem(
                  "selected_bonus",
                  JSON.stringify(newBonus)
                );
              }}
              options={options}
              watch={watch}
              {...field}
            />
          )}
        />
      </HeadTableau>
      {AllBonus}
      <TexteInfo>
        *Sauf indication contraire, les bonus sont calculés sur le chiffre
        d'affaires total réalisé avec le partenaire.
      </TexteInfo>
    </Tableau>
  );
}
